import React, { ChangeEvent } from "react";
import Dropdown from "antd/es/dropdown";
import Input from "common/Inputs/Input";
import styled, { useTheme } from "styled-components";
import useMultiSymbolsSelector, {
  SymbolOptionData,
} from "@shared/features/bots/BotForm/useMultiSymbolsSelector";
import Checkbox from "common/Checkbox/Checkbox";
import PairButtons from "features/bots/BotForm/General/PairButtons";
import CoinsIconsGroup from "features/portfolio/CoinsIconsGroup";
import Select from "common/Select/Select";
import { isSymbol } from "@shared/types/utils";
import Text from "common/Typography/Text.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import BottomDrawer from "common/Drawers/BottomDrawer.tsx";
import { useSelectSearch } from "common/Select/hooks.tsx";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import { INCLUDE_NEW_SYMBOLS_TOOLTIP } from "@shared/common/Tooltips/constants.ts";
import FlatList from "common/Lists/FlatList.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import { phoneMinWidth } from "common/NewLook/layout.ts";

const MultiSymbolSelect = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { searchComponent, searchValue, setSearchValue } = useSelectSearch();
  const {
    pairOptions,
    symbolOptions,
    isOpen,
    setIsOpen,
    searchInputData,
    selectAllData,
    inputHoverRef,
    symbols,
    exchange,
    isSignalsBot,
    includeNewSymbols,
    setIncludeNewSymbols,
    account,
  } = useMultiSymbolsSelector({ searchValue, setSearchValue });
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const emptyStates = useEmptyState();
  const Empty = emptyStates[EmptyStatesType.Empty];

  const getSymbolsPlaceholder = () => {
    if (isSymbol(symbols) || symbols.length) {
      return (
        <CoinsIconsGroup
          symbols={isSymbol(symbols) ? [] : symbols}
          exchange={exchange}
          size={22}
          style={{ marginLeft: 8 }}
          maxCount={8}
        />
      );
    }
    return isSignalsBot
      ? t("placeholders.choose_symbols")
      : t("placeholders.choose_symbol");
  };

  const renderSymbol = ({
    item,
    index,
  }: {
    item: SymbolOptionData;
    index: number;
  }) => {
    if (index === 0) {
      return (
        <CheckboxRow
          style={{ padding: "4px 0 12px 0" }}
          onClick={selectAllData.onClick}
        >
          <Checkbox
            checked={selectAllData.isSelected}
            style={{ marginRight: 12 }}
            $size={18}
          />
          {selectAllData.text}
        </CheckboxRow>
      );
    }
    return (
      <CheckboxRow onClick={item.onClick}>
        <Checkbox
          checked={item.isSelected}
          style={{ marginRight: 12 }}
          $size={18}
        />
        {item.symbol}
      </CheckboxRow>
    );
  };

  const getDropdownContent = () => {
    if (!account) {
      return (
        <EmptyWrapper>
          <Empty
            style={{
              height: 60,
              width: "100%",
              marginBottom: 8,
            }}
          />
          <Text>Select an account to continue</Text>
        </EmptyWrapper>
      );
    }
    return (
      <>
        <CheckboxWrapper
          onClick={() => setIncludeNewSymbols(!includeNewSymbols)}
        >
          <Checkbox
            checked={includeNewSymbols}
            style={{ marginRight: 12 }}
            $size={18}
          />
          <Text>{t("labels.include_newly_listed_symbols")}</Text>
          <HelperTooltip
            {...INCLUDE_NEW_SYMBOLS_TOOLTIP}
            iconStyle={{ color: theme.colors.primaryText }}
            description={t(INCLUDE_NEW_SYMBOLS_TOOLTIP.description)}
            title={t(INCLUDE_NEW_SYMBOLS_TOOLTIP.title)}
          />
        </CheckboxWrapper>
        <PairButtons pairOptions={pairOptions} bordered={true} />
        <SymbolsWrapper>
          <div style={{ height: isBottomNavigator ? "45vh" : 200 }}>
            <FlatList
              data={[selectAllData, ...symbolOptions]}
              renderContent={renderSymbol}
              gap={0}
            />
          </div>
        </SymbolsWrapper>
      </>
    );
  };

  if (isBottomNavigator) {
    return (
      <BottomDrawer
        title={t("labels.symbol")}
        triggerElement={
          <Select
            open={false}
            placeholder={getSymbolsPlaceholder()}
            style={{ width: "100%" }}
          />
        }
        rightButtons={account ? searchComponent : undefined}
        content={getDropdownContent()}
        contentStyle={{ padding: 0 }}
      />
    );
  }

  const dropdownRender = (
    <DropdownContentWrapper>{getDropdownContent()}</DropdownContentWrapper>
  );

  return (
    <Dropdown
      popupRender={() => dropdownRender}
      trigger={["click"]}
      open={isOpen}
      onOpenChange={(open) => {
        if (open || !inputHoverRef.current) {
          setIsOpen(open);
        }
      }}
    >
      {isOpen ? (
        <StyledInput
          $isDropdownOpen={true}
          value={searchInputData.value}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            searchInputData.onChange(e.target.value)
          }
          ref={searchInputData.ref}
          onMouseOver={searchInputData.onMouseOver}
          onMouseOut={searchInputData.onMouseOut}
        />
      ) : (
        <Select
          placeholder={getSymbolsPlaceholder()}
          style={{ width: "100%" }}
        />
      )}
    </Dropdown>
  );
};

export default MultiSymbolSelect;

const DropdownContentWrapper = styled.div`
  background-color: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: rgba(100, 100, 111, 0.2) 0 7px 29px 0;
  border-radius: 8px;
  min-width: 200px;
  max-width: 350px;
`;
const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 14px 14px 4px 14px;
`;
const SymbolsWrapper = styled.div`
  padding-top: 6px;
  overflow-y: auto;
  @media (min-width: ${phoneMinWidth}) {
    max-height: 250px;
  }
`;
const CheckboxRow = styled.div`
  padding: 6px 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(p) => p.theme.colors.primaryText};
`;
const StyledInput = styled(Input)<{ $isDropdownOpen?: boolean }>`
  ${(p) => !p.$isDropdownOpen && `cursor: pointer;`};
`;
const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 180px;
  padding: 20px;
`;
